// Zen Page - Simplified Site Analysis Interface
import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import scrapeJobApi from '../services/scrapeJobApi';
import analysisJobApi from '../services/analysisJobApi';
import fileApi from '../services/fileApi';
import adminApi from '../services/adminApi';
import { browserSessionsApi } from '../services/api';
import Loading from '../components/Loading';
import JobProgress from '../components/JobProgress';

const Zen = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [completedScrapeJobs, setCompletedScrapeJobs] = useState([]);
  const [error, setError] = useState(null);
  const [zenJobs, setZenJobs] = useState([]);
  
  // Admin settings
  const [adminSettings, setAdminSettings] = useState(null);
  
  // Form state
  const [clientUrl, setClientUrl] = useState('');
  const [competitorUrls, setCompetitorUrls] = useState(['']);
  const [selectedOptions, setSelectedOptions] = useState({
    executiveSummary: true,
    visualSiteMap: false,
    contentModel: false,
    competitiveAnalysis: false,
  });
  
  // Files section
  const [allFiles, setAllFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [fileError, setFileError] = useState(null);
  const fileInputRef = useRef(null);
  
  // URL suggestions based on completed scrape jobs
  const [clientUrlSuggestions, setClientUrlSuggestions] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const clientInputRef = useRef(null);
  
  // Competitor URL suggestions
  const [competitorUrlSuggestions, setCompetitorUrlSuggestions] = useState([]);
  const [showCompetitorSuggestions, setShowCompetitorSuggestions] = useState(false);
  const [activeCompetitorIndex, setActiveCompetitorIndex] = useState(null);
  
  // Track if we're submitting a job
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Browser sessions state
  const [browserSessions, setBrowserSessions] = useState([]);
  const [browserSessionsError, setBrowserSessionsError] = useState(null);

  // Load completed scrape jobs on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        console.log('Zen: Loading initial data...');
        setIsLoading(true);
        
        // Fetch admin settings
        try {
          const settings = await adminApi.getSettings();
          console.log('Zen: Fetched admin settings:', settings);
          setAdminSettings(settings);
        } catch (settingsErr) {
          console.error('Error fetching admin settings:', settingsErr);
          // Continue execution even if admin settings fail to load
        }
        
        // Fetch all user uploaded files
        await fetchUserFiles();
        
        // Fetch completed scrape jobs (for URL suggestions)
        const scrapeJobs = await scrapeJobApi.getScrapeJobs({ all: true });
        console.log('Zen: Fetched scrape jobs:', scrapeJobs);
        
        // Filter only completed scrape jobs
        const completed = scrapeJobs.filter(job => job.status === 'completed');
        console.log('Zen: Completed scrape jobs:', completed);
        setCompletedScrapeJobs(completed);
        
        // Extract unique URLs for suggestions
        const urls = [...new Set(completed.map(job => job.targetUrl))];
        console.log('Zen: Available URL suggestions:', urls);
        setClientUrlSuggestions(urls);
        setCompetitorUrlSuggestions(urls);
        
        // Also attempt to load existing Zen jobs
        loadZenJobs();

        // Fetch browser sessions
        await fetchBrowserSessions();
      } catch (err) {
        setError(err.message);
        console.error('Error fetching initial data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Function to fetch browser sessions
  const fetchBrowserSessions = async () => {
    try {
      setBrowserSessionsError(null);
      console.log('Zen: Fetching browser sessions...');
      
      const sessions = await browserSessionsApi.getBrowserSessions();
      console.log('Zen: Browser sessions response:', sessions);
      
      if (sessions && Array.isArray(sessions)) {
        setBrowserSessions(sessions);
      } else {
        console.log('Zen: No browser sessions returned or invalid format');
        setBrowserSessions([]);
      }
    } catch (err) {
      console.error('Error fetching browser sessions:', err);
      setBrowserSessionsError(err.message);
      setBrowserSessions([]);
    }
  };
  
  // Function to fetch user uploaded files
  const fetchUserFiles = async () => {
    try {
      setIsLoadingFiles(true);
      setFileError(null);
      console.log('Zen: Loading user uploaded files...');
      
      // Use userUploaded=true parameter to get only user uploaded files
      const files = await fileApi.getFiles({ userUploaded: true });
      console.log('Zen: Fetched user uploaded files:', files);
      
      if (files && Array.isArray(files)) {
        setAllFiles(files);
      } else {
        console.log('Zen: No user files returned or invalid format');
        setAllFiles([]);
      }
    } catch (err) {
      console.error('Error fetching user files:', err);
      setFileError(err.message);
      setAllFiles([]);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  // Load previously created Zen jobs from localStorage
  const loadZenJobs = () => {
    console.log('Zen: Loading saved jobs from localStorage');
    const savedJobs = localStorage.getItem('zenJobs');
    if (savedJobs) {
      try {
        const parsedJobs = JSON.parse(savedJobs);
        console.log('Zen: Parsed saved jobs:', parsedJobs);
        // Sort jobs by creation date (most recent first)
        const sortedJobs = parsedJobs.sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        
        // Modify job expansion state: only keep most recent job expanded
        if (sortedJobs.length > 0) {
          // Keep the first (newest) job expanded
          sortedJobs[0].expanded = true;
          
          // Collapse all other jobs
          for (let i = 1; i < sortedJobs.length; i++) {
            sortedJobs[i].expanded = false;
            
            // Collapse analysis job items for non-newest jobs
            // if (sortedJobs[i].analysisJobs && sortedJobs[i].analysisJobs.length > 0) {
            //   sortedJobs[i].analysisJobs.forEach(analysis => {
            //     analysis.expanded = false;
            //   });
            // }
          }
        }
        
        console.log('Zen: Sorted jobs to display (with expansion modified):', sortedJobs);
        setZenJobs(sortedJobs);
        
        // Resume monitoring for any in-progress jobs after page reload
        sortedJobs.forEach(job => {
          console.log('hi')
          // Check for any completed analysis jobs without results and load them
          if (job.status === 'completed' && job.analysisJobs && job.analysisJobs.length > 0) {
          console.log('hi2')

            job.analysisJobs.forEach(async (analysis) => {
            console.log('hi3')

              if (analysis.status === 'completed' && !analysis.resultText) {
                console.log('hi4')
                console.log(`Zen: Fetching missing result for completed analysis job ${analysis.jobId}`);
                try {
                  const updatedJob = await analysisJobApi.getAnalysisJob(analysis.jobId);
                  console.log('updatedJob:')
                  console.log(updatedJob)
                  if (updatedJob.resultText) {
                    analysis.resultText = updatedJob.resultText;
                    analysis.expanded = true;
                    console.log(`Zen: Successfully loaded missing result for analysis job ${analysis.jobId}`);
                    updateZenJob(job);
                  } else {
                    console.log(`Zen: No result found for completed analysis job ${analysis.jobId}`);
                    // analysis.resultText = "No results available. View the full analysis for details.";
                    updateZenJob(job);
                  }
                } catch (err) {
                  console.error(`Error fetching result for analysis job ${analysis.jobId}:`, err);
                  // analysis.resultText = "Error loading results. Please try viewing the full analysis.";
                  updateZenJob(job);
                }
              }
            });
          }
          
          // Resume monitoring for in-progress jobs
          if (job.status === 'pending' || job.status === 'scraping') {
            console.log('Zen: Resuming monitoring for in-progress job:', job.id);
            monitorScrapeJobs(job);
          } else if (job.status === 'analyzing') {
            console.log('Zen: Resuming analysis monitoring for job:', job.id);
            monitorAnalysisJobs(job);f
          }
        });
      } catch (err) {
        console.error('Error parsing saved Zen jobs:', err);
      }
    } else {
      console.log('Zen: No saved jobs found');
    }
  };

  // Save Zen jobs to localStorage
  const saveZenJobs = (jobs) => {
    console.log('Zen: Saving jobs to localStorage:', jobs);
    localStorage.setItem('zenJobs', JSON.stringify(jobs));
    // Verify what was saved by reading it back
    const savedJobs = localStorage.getItem('zenJobs');
    if (savedJobs) {
      console.log('Zen: Verified saved jobs:', JSON.parse(savedJobs));
    }
  };

  // Toggle an analysis option
  const toggleOption = (option) => {
    setSelectedOptions(prev => {
      // If turning competitive analysis off, clear competitor URLs
      if (option === 'competitiveAnalysis' && prev.competitiveAnalysis) {
        setCompetitorUrls(['']);
        setShowCompetitorSuggestions(false);
        setActiveCompetitorIndex(null);
      } else if (option === 'competitiveAnalysis' && !prev.competitiveAnalysis) {
        // When re-enabling competitive analysis, don't need to reset suggestions
        // They'll be set when the input is focused
      }
      return { ...prev, [option]: !prev[option] };
    });
  };
  
  // Handle file selection toggle
  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prevSelectedFiles => {
      if (prevSelectedFiles.includes(fileId)) {
        return prevSelectedFiles.filter(id => id !== fileId);
      } else {
        return [...prevSelectedFiles, fileId];
      }
    });
  };
  
  // Handle file deletion
  const handleDeleteFile = async (fileId) => {
    try {
      console.log(`Zen: Deleting file ${fileId}`);
      setFileError(null);
      
      // Remove from selected files if it was selected
      if (selectedFiles.includes(fileId)) {
        setSelectedFiles(selectedFiles.filter(id => id !== fileId));
      }
      
      // Call the API to delete the file
      await fileApi.deleteFile(fileId);
      
      // Refresh the file list
      await fetchUserFiles();
      
      console.log(`Zen: File ${fileId} deleted successfully`);
    } catch (err) {
      console.error(`Error deleting file ${fileId}:`, err);
      setFileError(`Error deleting file: ${err.message}`);
    }
  };
  
  // Handle file upload
  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    try {
      setIsSubmitting(true);
      setFileError(null);
      
      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`Zen: Uploading file ${file.name}...`);
        
        // Use the uploadFileToFileSet function but without a fileSetId
        // This will create a standalone file
        const result = await fileApi.uploadFileToFileSet(file, null);
        console.log('Zen: File upload result:', result);
      }
      
      // Refresh the file list
      await fetchUserFiles();
      
    } catch (err) {
      console.error('Error uploading files:', err);
      setFileError(`Error uploading files: ${err.message}`);
    } finally {
      setIsSubmitting(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle competitor URL changes
  const handleCompetitorUrlChange = (index, value) => {
    const updated = [...competitorUrls];
    updated[index] = value;
    setCompetitorUrls(updated);
    
    // Show suggestions if we have a partial match
    if (value) {
      const filtered = clientUrlSuggestions.filter(url => 
        url.toLowerCase().includes(value.toLowerCase())
      );
      setCompetitorUrlSuggestions(filtered.length > 0 ? filtered : []);
      setShowCompetitorSuggestions(filtered.length > 0);
      setActiveCompetitorIndex(index);
    } else {
      // When input is empty, show all suggestions
      setCompetitorUrlSuggestions(completedScrapeJobs.map(job => job.targetUrl));
      setShowCompetitorSuggestions(false);
    }
  };

  // Add new competitor URL input
  const addCompetitorUrl = () => {
    setCompetitorUrls([...competitorUrls, '']);
    // Reset active competitor index to ensure suggestions work for new field
    setActiveCompetitorIndex(null);
    setShowCompetitorSuggestions(false);
  };

  // Remove competitor URL input
  const removeCompetitorUrl = (index) => {
    const updated = competitorUrls.filter((_, i) => i !== index);
    if (updated.length === 0) {
      updated.push(''); // Always keep at least one input
    }
    setCompetitorUrls(updated);
    
    // Reset suggestion state when removing a competitor URL
    setShowCompetitorSuggestions(false);
    setActiveCompetitorIndex(null);
  };

  // Handle client URL input change
  const handleClientUrlChange = (e) => {
    const value = e.target.value;
    setClientUrl(value);
    
    // Show suggestions if we have a partial match
    if (value) {
      const filtered = clientUrlSuggestions.filter(url => 
        url.toLowerCase().includes(value.toLowerCase())
      );
      setClientUrlSuggestions(filtered.length > 0 ? filtered : []);
      setShowClientSuggestions(filtered.length > 0);
    } else {
      // When input is empty, show all suggestions
      setClientUrlSuggestions(completedScrapeJobs.map(job => job.targetUrl));
      setShowClientSuggestions(false);
    }
  };

  // Select a suggestion for client URL
  const selectClientUrlSuggestion = (url) => {
    setClientUrl(url);
    setShowClientSuggestions(false);
  };

  // Handle competitor URL suggestion selection
  const selectCompetitorUrlSuggestion = (url, index) => {
    const updated = [...competitorUrls];
    updated[index] = url;
    setCompetitorUrls(updated);
    setShowCompetitorSuggestions(false);
  };

  // Update a Zen job in the state and localStorage
  const updateZenJob = (updatedJob) => {
    console.log('updateZenJob', updatedJob.id);
    
    // Create a deep copy of the current zenJobs array to avoid mutation issues
    const jobsCopy = JSON.parse(JSON.stringify(zenJobs));
    
    // Check if the job exists in the current state
    const jobExists = jobsCopy.some(job => job.id === updatedJob.id);
    
    let updated;
    if (jobExists) {
      // Update the existing job
      console.log(`Job ${updatedJob.id} exists, updating it`);
      updated = jobsCopy.map(job => 
        job.id === updatedJob.id ? updatedJob : job
      );
    } else {
      // Job doesn't exist, add it to the beginning of the array (newest first)
      console.log(`Job ${updatedJob.id} doesn't exist in state, adding it to the front`);
      updated = [updatedJob, ...jobsCopy];
    }
    
    console.log('Updated jobs count:', updated.length);
    
    // First save to localStorage to ensure persistence
    saveZenJobs(updated);
    
    // Then update the state
    setZenJobs(updated);
  };

  // Start the analysis process
  const startAnalysis = async () => {
    console.log('Zen: Starting analysis process');
    
    // A client URL is required
    if (!clientUrl) {
      console.log('Zen: Error - No client URL provided');
      setError('Client URL is required');
      return;
    }
    
    // If competitive analysis is selected and we're using URLs, validate competitor URLs
    if (selectedOptions.competitiveAnalysis && 
        (!competitorUrls[0] || competitorUrls.some(url => !url))) {
      console.log('Zen: Error - Missing competitor URLs');
      setError('All competitor URLs must be filled for competitive analysis');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Collect all URLs that need to be scraped
      const allUrls = [clientUrl];
      if (selectedOptions.competitiveAnalysis) {
        allUrls.push(...competitorUrls.filter(url => url));
      }
      console.log('Zen: URLs to analyze:', allUrls);
      
      // Check which URLs have already been scraped
      const scrapedUrls = completedScrapeJobs.map(job => job.targetUrl);
      console.log('Zen: Already scraped URLs:', scrapedUrls);
      const urlsToScrape = allUrls.filter(url => !scrapedUrls.includes(url));
      console.log('Zen: New URLs that need scraping:', urlsToScrape);
      
      // Create a new Zen job entry
      const zenJob = {
        id: `zen-${Date.now()}`,
        createdAt: new Date().toISOString(),
        clientUrl,
        competitorUrls: selectedOptions.competitiveAnalysis ? [...competitorUrls] : [],
        options: { ...selectedOptions },
        status: 'pending',
        scrapeJobs: [],
        analysisJobs: [],
        selectedFileIds: [...selectedFiles], // Include user-selected file IDs
        expanded: true, // Set expanded to true by default for new jobs
        progressItems: [
          {
            text: 'Started Zen analysis process',
            timestamp: new Date().toISOString()
          }
        ]
      };
      console.log('Zen: Created new zenJob:', zenJob);
      
      // Add to Zen jobs list
      const updatedZenJobs = [zenJob, ...zenJobs];
      console.log('Zen: About to update zenJobs list with:', updatedZenJobs);
      setZenJobs(updatedZenJobs);
      saveZenJobs(updatedZenJobs);
      console.log('Zen: Updated zenJobs list, current state may not reflect changes immediately due to React\'s asynchronous state updates');
      
      // Start scrape jobs for URLs that haven't been scraped yet
      if (urlsToScrape.length > 0) {
        console.log('Zen: Starting scrape jobs for new URLs');
        // Update progress
        zenJob.progressItems.push({
          text: `Starting scrape for ${urlsToScrape.length} URL(s)`,
          timestamp: new Date().toISOString()
        });
        updateZenJob(zenJob);
        
        // Create scrape jobs in sequence
        for (const url of urlsToScrape) {
          zenJob.progressItems.push({
            text: `Creating scrape job for ${url}`,
            timestamp: new Date().toISOString()
          });
          updateZenJob(zenJob);
          
          console.log(`Zen: Creating scrape job for URL: ${url}`);
          const scrapeJob = await scrapeJobApi.createScrapeJob({
            targetUrl: url,
            maxLinksToCrawl: 2000,
            parallelSessions: 3
          });
          console.log(`Zen: Scrape job created:`, scrapeJob);
          
          zenJob.scrapeJobs.push({
            jobId: scrapeJob.jobId,
            targetUrl: url,
            status: 'pending'
          });
          console.log(`Zen: Added scrape job to zenJob.scrapeJobs`);
          
          zenJob.progressItems.push({
            text: `Scrape job created for ${url}`,
            timestamp: new Date().toISOString()
          });
          updateZenJob(zenJob);
        }
      }
      
      // Start monitoring scrape jobs
      zenJob.status = 'scraping';
      updateZenJob(zenJob);
      console.log('Zen: Updated job status to scraping');
      
      // If there were no URLs to scrape, start analysis immediately
      if (urlsToScrape.length === 0) {
        console.log('Zen: No new URLs to scrape, starting analysis immediately');
        startAnalysisJobs(zenJob);
        // return;
      }
      else {
        // Continue with status checking in the background
        console.log('Zen: Starting background monitoring of scrape jobs');
        monitorScrapeJobs(zenJob);
      }
     
      
    } catch (err) {
      setError(err.message);
      console.error('Error starting analysis:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Monitor scrape jobs and start analysis when they complete
  const monitorScrapeJobs = async (zenJob) => {
    console.log('Zen: In monitorScrapeJobs function for job:', zenJob.id);
    
    // Skip if job was already completed or had an error
    if (zenJob.status === 'completed' || zenJob.status === 'error') {
      console.log(`Zen: Skipping monitoring for job ${zenJob.id} as it's already in ${zenJob.status} state`);
      return;
    }
    
    // If no scrape jobs, skip to analysis
    if (!zenJob.scrapeJobs || zenJob.scrapeJobs.length === 0) {
      console.log('Zen: No scrape jobs to monitor, proceeding to analysis');
      zenJob.progressItems.push({
        text: 'No scrape jobs needed, proceeding to analysis',
        timestamp: new Date().toISOString()
      });
      updateZenJob(zenJob);
      startAnalysisJobs(zenJob);
      return;
    }
    
    try {
      // Update job status to make sure it's in scraping mode
      if (zenJob.status === 'pending') {
        zenJob.status = 'scraping';
        zenJob.progressItems.push({
          text: 'Monitoring scrape jobs',
          timestamp: new Date().toISOString()
        });
        updateZenJob(zenJob);
      }
      
      // Get list of pending scrape job IDs
      let pendingScrapeJobs = zenJob.scrapeJobs.filter(job => 
        job.status !== 'completed' && job.status !== 'failed'
      );
      console.log('Zen: Pending scrape jobs to monitor:', pendingScrapeJobs);
      
      // If we're resuming after a reload, re-check all jobs once
      if (pendingScrapeJobs.length > 0) {
        zenJob.progressItems.push({
          text: `Checking status of ${pendingScrapeJobs.length} pending scrape jobs`,
          timestamp: new Date().toISOString()
        });
        updateZenJob(zenJob);
      }
      
      while (pendingScrapeJobs.length > 0) {
        // Wait 5 seconds before checking again
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        // Check each pending job
        for (const job of pendingScrapeJobs) {
          try {
            console.log(`Zen: Checking status of scrape job ${job.jobId} for ${job.targetUrl}`);
            const updatedJob = await scrapeJobApi.getScrapeJob(job.jobId);
            console.log(`Zen: Current status of job ${job.jobId}: ${updatedJob.status}`);
            
            // Update job status
            if (updatedJob.status !== job.status) {
              console.log(`Zen: Job status changed from ${job.status} to ${updatedJob.status}`);
              job.status = updatedJob.status;
              
              zenJob.progressItems.push({
                text: `Scrape job for ${job.targetUrl} is now ${updatedJob.status}`,
                timestamp: new Date().toISOString()
              });
              
              updateZenJob(zenJob);
            }
          } catch (err) {
            console.error(`Error checking scrape job ${job.jobId}:`, err);
            job.status = 'error';
            zenJob.progressItems.push({
              text: `Error checking scrape job ${job.jobId}: ${err.message}`,
              timestamp: new Date().toISOString()
            });
            updateZenJob(zenJob);
          }
        }
        
        // Filter pending jobs again
        pendingScrapeJobs = zenJob.scrapeJobs.filter(job => 
          job.status !== 'completed' && job.status !== 'failed' && job.status !== 'error'
        );
        console.log('Zen: Updated pending scrape jobs:', pendingScrapeJobs);
      }
      
      // All scrape jobs completed or failed, start analysis jobs
      console.log('Zen: All scrape jobs finished, proceeding to analysis');
      startAnalysisJobs(zenJob);
      
    } catch (err) {
      console.error('Error monitoring scrape jobs:', err);
      zenJob.progressItems.push({
        text: `Error monitoring scrape jobs: ${err.message}`,
        timestamp: new Date().toISOString()
      });
      zenJob.status = 'error';
      updateZenJob(zenJob);
    }
  };
  
  // Start analysis jobs
  const startAnalysisJobs = async (zenJob) => {
    console.log('Zen: Starting analysis jobs function');
    try {
      // Update status
      zenJob.status = 'analyzing';
      zenJob.progressItems.push({
        text: 'Starting analysis jobs',
        timestamp: new Date().toISOString()
      });
      updateZenJob(zenJob);
      console.log('Zen: Set job status to analyzing');
      
      // Map of URLs to scrape job IDs (both newly created and pre-existing)
      const urlToJobId = {};
      
      // Add newly created scrape jobs
      zenJob.scrapeJobs.forEach(job => {
        if (job.status === 'completed') {
          urlToJobId[job.targetUrl] = job.jobId;
          console.log(`Zen: Added completed scrape job for ${job.targetUrl} to map`);
        }
      });
      
      // Add pre-existing scrape jobs
      completedScrapeJobs.forEach(job => {
        urlToJobId[job.targetUrl] = job.jobId;
        console.log(`Zen: Added pre-existing scrape job for ${job.targetUrl} to map`);
      });
      
      console.log('Zen: URL to job ID mapping:', urlToJobId);
      
      // Get client scrape job ID - URL is required
      const clientScrapeJobId = urlToJobId[zenJob.clientUrl];
      console.log(`Zen: Looking for client scrape job ID for ${zenJob.clientUrl}: ${clientScrapeJobId}`);
      
      if (!clientScrapeJobId) {
        console.log(`Zen: ERROR - Could not find completed scrape job for ${zenJob.clientUrl}`);
        throw new Error(`Could not find completed scrape job for ${zenJob.clientUrl}`);
      }
      
      // Let our functions know that we have selected file IDs in the zenJob object
      console.log(`Zen: Using ${(zenJob.selectedFileIds || []).length} user-selected files for analysis`);
      
      // Create analysis jobs based on selected options
      const analysisPromises = [];
      console.log('Zen: Preparing to create analysis jobs based on options:', zenJob.options);
      
      // 1. Executive Summary
      if (zenJob.options.executiveSummary) {
        console.log('Zen: Creating Executive Summary analysis');
        zenJob.progressItems.push({
          text: 'Creating Executive Summary analysis',
          timestamp: new Date().toISOString()
        });
        updateZenJob(zenJob);
        
        const executiveSummaryPromise = createAnalysisJob(
          'Executive Summary', 
          adminSettings?.promptOptions?.executiveSummary || 'Provide a comprehensive overview of the website\'s content, features, and recommendations for improvement.',
          clientScrapeJobId,
          zenJob
        ).then(job => {
          console.log('Zen: Executive Summary job created:', job);
          zenJob.analysisJobs.push({
            jobId: job.jobId,
            type: 'executiveSummary',
            status: 'pending',
            expanded: true // Always expanded for new jobs
          });
          
          zenJob.progressItems.push({
            text: 'Executive Summary analysis job created',
            timestamp: new Date().toISOString()
          });
          updateZenJob(zenJob);
          return job;
        });
        
        analysisPromises.push(executiveSummaryPromise);
      }
      
      // 2. Visual Site Map
      if (zenJob.options.visualSiteMap) {
        zenJob.progressItems.push({
          text: 'Creating Visual Site Map analysis',
          timestamp: new Date().toISOString()
        });
        updateZenJob(zenJob);
        
        const siteMapPromise = createAnalysisJob(
          'Visual Site Map', 
          adminSettings?.promptOptions?.sitemap || 'Create a visual representation of the website\'s structure and navigation hierarchy.',
          clientScrapeJobId,
          zenJob
        ).then(job => {
          zenJob.analysisJobs.push({
            jobId: job.jobId,
            type: 'visualSiteMap',
            status: 'pending',
            expanded: true // Always expanded for new jobs
          });
          
          zenJob.progressItems.push({
            text: 'Visual Site Map analysis job created',
            timestamp: new Date().toISOString()
          });
          updateZenJob(zenJob);
          return job;
        });
        
        analysisPromises.push(siteMapPromise);
      }
      
      // 3. Content Model
      if (zenJob.options.contentModel) {
        zenJob.progressItems.push({
          text: 'Creating Content Model analysis',
          timestamp: new Date().toISOString()
        });
        updateZenJob(zenJob);
        
        const contentModelPromise = createAnalysisJob(
          'Content Model', 
          adminSettings?.promptOptions?.contentTypes || 'Provide a detailed breakdown of content types, templates, and relationships within the site.',
          clientScrapeJobId,
          zenJob
        ).then(job => {
          zenJob.analysisJobs.push({
            jobId: job.jobId,
            type: 'contentModel',
            status: 'pending',
            expanded: true // Always expanded for new jobs
          });
          
          zenJob.progressItems.push({
            text: 'Content Model analysis job created',
            timestamp: new Date().toISOString()
          });
          updateZenJob(zenJob);
          return job;
        });
        
        analysisPromises.push(contentModelPromise);
      }
      
      // 4. Competitive Analysis
      if (zenJob.options.competitiveAnalysis && zenJob.competitorUrls.length > 0) {
        zenJob.progressItems.push({
          text: 'Creating Competitive Analysis',
          timestamp: new Date().toISOString()
        });
        updateZenJob(zenJob);
        
        // Get competitor scrape job IDs
        const competitorScrapeJobIds = zenJob.competitorUrls
          .filter(url => url && urlToJobId[url])
          .map(url => urlToJobId[url]);
        
        if (competitorScrapeJobIds.length > 0) {
          const competitiveAnalysisPromise = createCompetitiveAnalysis(
            clientScrapeJobId,
            competitorScrapeJobIds,
            zenJob
          ).then(job => {
            zenJob.analysisJobs.push({
              jobId: job.jobId,
              type: 'competitiveAnalysis',
              status: 'pending',
              expanded: true // Always expanded for new jobs
            });
            
            zenJob.progressItems.push({
              text: 'Competitive Analysis job created',
              timestamp: new Date().toISOString()
            });
            updateZenJob(zenJob);
            return job;
          });
          
          analysisPromises.push(competitiveAnalysisPromise);
        }
      }
      
      // Wait for all analysis jobs to be created
      await Promise.all(analysisPromises);
      
      // Start monitoring analysis jobs
      monitorAnalysisJobs(zenJob);
      
    } catch (err) {
      console.error('Error starting analysis jobs:', err);
      zenJob.progressItems.push({
        text: `Error starting analysis jobs: ${err.message}`,
        timestamp: new Date().toISOString()
      });
      zenJob.status = 'error';
      updateZenJob(zenJob);
    }
  };
  
  // Helper function to create a standard analysis job
  const createAnalysisJob = async (title, prompt, scrapeJobId, zenJob) => {
    console.log(`Zen: Creating analysis job "${title}" for scrape job ${scrapeJobId}`);
    
    // Get user-selected file IDs from zenJob
    const userFileIds = zenJob.selectedFileIds || [];
    console.log(`Zen: Using ${userFileIds.length} user-selected files from zenJob`);
    
    let allFileIds = [...userFileIds]; // Start with user-selected files
    
    // Get files from the scrape job if we have one
    if (scrapeJobId) {
      const files = await getFilesForScrapeJob(scrapeJobId, zenJob);
      console.log(`Zen: Retrieved ${files.length} files from scrape job with id ` + scrapeJobId);
      
      // Add scrape job file IDs to our collection
      const scrapeFileIds = files.map(file => file.fileId);
      allFileIds = [...allFileIds, ...scrapeFileIds];
    }
    
    // Create the analysis job
    console.log(`Zen: Calling analysisJobApi.createAnalysisJob with ${allFileIds.length} total files`);
    console.log(`Zen: File IDs for analysis:`, allFileIds);
    
    return await analysisJobApi.createAnalysisJob({
      title,
      files: allFileIds,
      prompt,
      modelName: adminSettings?.model || "gemini-2.0-flash",
      temperature: 0.7,
      maxOutputTokens: 6000
    });
  };
  
  // Helper function to create a competitive analysis job
  const createCompetitiveAnalysis = async (clientScrapeJobId, competitorScrapeJobIds, zenJob) => {
    console.log(`Zen: Creating competitive analysis job`);
    console.log(`Zen: Client scrape job ID: ${clientScrapeJobId}`);
    console.log(`Zen: Competitor scrape job IDs: ${competitorScrapeJobIds.join(', ')}`);
    
    // Get user-selected file IDs from zenJob
    const userFileIds = zenJob.selectedFileIds || [];
    console.log(`Zen: Using ${userFileIds.length} user-selected files from zenJob`);
    
    // Get files from all scrape jobs
    let scrapedFiles = [];
    
    // Get client files if we have a client scrape job
    if (clientScrapeJobId) {
      console.log(`Zen: Getting client files from scrape job ${clientScrapeJobId}`);
      const clientFiles = await getFilesForScrapeJob(clientScrapeJobId, zenJob);
      console.log(`Zen: Retrieved ${clientFiles.length} client files`);
      scrapedFiles.push(...clientFiles);
    }
    
    // Get competitor files
    for (const jobId of competitorScrapeJobIds) {
      console.log(`Zen: Getting competitor files from scrape job ${jobId}`);
      const files = await getFilesForScrapeJob(jobId, zenJob);
      console.log(`Zen: Retrieved ${files.length} files for competitor job ${jobId}`);
      scrapedFiles.push(...files);
    }
    
    // Create the analysis job with all files
    const scrapedFileIds = scrapedFiles.map(file => file.fileId);
    const allFileIds = [...scrapedFileIds, ...userFileIds];
    
    console.log(`Zen: Creating competitive analysis with ${scrapedFileIds.length} scraped files and ${userFileIds.length} user files`);
    console.log(`Zen: Combined file IDs for competitive analysis:`, allFileIds);
    
    return await analysisJobApi.createAnalysisJob({
      title: "Competitive Analysis",
      files: allFileIds,
      prompt: adminSettings?.promptOptions?.competitorAnalysis || "Compare the client website against competitor websites to identify strengths, weaknesses, and opportunities.",
      modelName: adminSettings?.model || "gemini-2.0-flash",
      temperature: 0.7,
      maxOutputTokens: 6000
    });
  };
  
  // Helper function to get files for a scrape job
  const getFilesForScrapeJob = async (scrapeJobId, currentZenJob) => {
    console.log(`Zen: Getting files for scrape job ${scrapeJobId}`);
    try {
      // Use the fileApi to get files for the scrape job
      console.log(`Zen: Calling fileApi.getFilesByScrapeJob for ${scrapeJobId}`);
      const files = await fileApi.getFilesByScrapeJob(scrapeJobId);
      console.log(`Zen: Retrieved ${files?.length || 0} files for scrape job ${scrapeJobId}`);
      
      // If no files found, return an empty array
      if (!files || files.length === 0) {
        console.log(`Zen: No files found for scrape job ${scrapeJobId}`);
        if (currentZenJob) {
          currentZenJob.progressItems.push({
            text: `No files found for scrape job ${scrapeJobId}`,
            timestamp: new Date().toISOString()
          });
          updateZenJob(currentZenJob);
        }
        return [];
      }
      
      // Group files by type
      const filesByType = fileApi.groupFilesByType(files);
      console.log(`Zen: Files grouped by type:`, Object.keys(filesByType).map(type => `${type}: ${filesByType[type]?.length || 0}`));
      
      // Prioritize certain file types for analysis
      const prioritizedFiles = [];
      
      // Add reduced HTML L2 files first (most concise)
      // if (filesByType['reduced-level2-html'] && filesByType['reduced-level2-html'].length > 0) {
      //   prioritizedFiles.push(...filesByType['reduced-level2-html']);
      // }
      
      // // Then add reduced HTML L1 files
      // if (filesByType['reduced-level1-html'] && filesByType['reduced-level1-html'].length > 0) {
      //   prioritizedFiles.push(...filesByType['reduced-level1-html']);
      // }
      
      // // Then add full HTML files if needed
      // if (filesByType['html'] && filesByType['html'].length > 0) {
      //   prioritizedFiles.push(...filesByType['html']);
      // }
      
      // Finally add screenshots
      if (filesByType['screenshot'] && filesByType['screenshot'].length > 0) {
        console.log(`Zen: Adding ${filesByType['screenshot'].length} screenshot files`);
        prioritizedFiles.push(...filesByType['screenshot']);
      }
      
      // // Add any other file types
      // Object.keys(filesByType).forEach(type => {
      //   if (!['reduced-level2-html', 'reduced-level1-html', 'html', 'screenshot'].includes(type)) {
      //     prioritizedFiles.push(...filesByType[type]);
      //   }
      // });
      
      // Limit to 100 files to prevent overwhelming the analysis
      // const limitedFiles = prioritizedFiles.slice(0, 100);
      const limitedFiles = prioritizedFiles;
      console.log(`Zen: Final selection: ${limitedFiles.length} files`);
      
      if (currentZenJob) {
        currentZenJob.progressItems.push({
          text: `Selected ${limitedFiles.length} files for analysis from scrape job ${scrapeJobId}`,
          timestamp: new Date().toISOString()
        });
        updateZenJob(currentZenJob);
      }
      
      return limitedFiles;
    } catch (err) {
      console.error(`Error getting files for scrape job ${scrapeJobId}:`, err);
      if (currentZenJob) {
        currentZenJob.progressItems.push({
          text: `Error getting files for scrape job ${scrapeJobId}: ${err.message}`,
          timestamp: new Date().toISOString()
        });
        updateZenJob(currentZenJob);
      }
      return [];
    }
  };
  
  // Monitor analysis jobs
  const monitorAnalysisJobs = async (zenJob) => {
    console.log('Zen: Starting to monitor analysis jobs for job:', zenJob.id);
    
    // Skip if job was already completed or had an error
    if (zenJob.status === 'completed' || zenJob.status === 'error') {
      console.log(`Zen: Skipping analysis monitoring for job ${zenJob.id} as it's already in ${zenJob.status} state`);
      return;
    }
    
    // Skip if no analysis jobs
    if (!zenJob.analysisJobs || zenJob.analysisJobs.length === 0) {
      console.log(`Zen: No analysis jobs found for job ${zenJob.id}`);
      zenJob.progressItems.push({
        text: 'No analysis jobs found, marking as completed',
        timestamp: new Date().toISOString()
      });
      zenJob.status = 'completed';
      updateZenJob(zenJob);
      return;
    }
    
    try {
      // Update job status to ensure it's in analyzing mode
      if (zenJob.status !== 'analyzing') {
        zenJob.status = 'analyzing';
        zenJob.progressItems.push({
          text: 'Monitoring analysis jobs',
          timestamp: new Date().toISOString()
        });
        updateZenJob(zenJob);
      }
      
      // Get list of pending analysis job IDs
      let pendingAnalysisJobs = zenJob.analysisJobs.filter(job => 
        job.status !== 'completed' && job.status !== 'failed'
      );
      console.log(`Zen: Found ${pendingAnalysisJobs.length} pending analysis jobs to monitor`);
      
      // If we're resuming after a reload, re-check all jobs once
      if (pendingAnalysisJobs.length > 0) {
        zenJob.progressItems.push({
          text: `Checking status of ${pendingAnalysisJobs.length} pending analysis jobs`,
          timestamp: new Date().toISOString()
        });
        updateZenJob(zenJob);
      }
      
      while (pendingAnalysisJobs.length > 0) {
        // Wait 5 seconds before checking again
        console.log(`Zen: Waiting 5 seconds before checking analysis jobs again...`);
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        // Check each pending job
        for (const job of pendingAnalysisJobs) {
          try {
            console.log(`Zen: Checking status of analysis job ${job.jobId} (type: ${job.type})`);
            const updatedJob = await analysisJobApi.getAnalysisJob(job.jobId);
            console.log(`Zen: Analysis job ${job.jobId} status: ${updatedJob.status}`);
            
            // Update job status
            if (updatedJob.status !== job.status) {
              console.log(`Zen: Job status changed from ${job.status} to ${updatedJob.status}`);
              job.status = updatedJob.status;
              
              // Save analysis result with fallback
              if (updatedJob.status === 'completed') {
                if (updatedJob.resultText) {
                  job.resultText = updatedJob.resultText;
                  job.expanded = true;
                  console.log(`Zen: Analysis result received for job ${job.jobId}`);
                } else {
                  // job.resultText = "Analysis completed but no results were returned. Please view the full analysis.";
                  console.log(`Zen: No result content found for completed job ${job.jobId}, using placeholder`);
                }
                
                // // Determine if this is the newest Zen job
                // const isNewestJob = zenJobs.length > 0 && zenJob.id === zenJobs[0].id;
                
                // // For the newest Zen job, keep analysis expanded when completed
                // // For older jobs, collapse them as before
                // if (isNewestJob) {
                //   // For newest job, preserve the expanded state (default is true from creation)
                //   console.log(`Zen: Preserving expansion for analysis job ${job.jobId} in newest Zen job`);
                //   // If job.expanded is undefined, set it to true (default expanded for new jobs)
                //   if (job.expanded === undefined) {
                //     job.expanded = true;
                //   }
                // } else {
                //   // For older jobs, collapse as before
                //   job.expanded = false;
                //   console.log(`Zen: Collapsing analysis job ${job.jobId} in older Zen job`);
                // }
              }
              
              zenJob.progressItems.push({
                text: `${getAnalysisTypeLabel(job.type)} is now ${updatedJob.status}`,
                timestamp: new Date().toISOString()
              });
              
              updateZenJob(zenJob);
            }
          } catch (err) {
            console.error(`Error checking analysis job ${job.jobId}:`, err);
            job.status = 'error';
            zenJob.progressItems.push({
              text: `Error checking analysis job ${job.jobId}: ${err.message}`,
              timestamp: new Date().toISOString()
            });
            updateZenJob(zenJob);
          }
        }
        
        // Filter pending jobs again
        pendingAnalysisJobs = zenJob.analysisJobs.filter(job => 
          job.status !== 'completed' && job.status !== 'failed' && job.status !== 'error'
        );
        console.log(`Zen: ${pendingAnalysisJobs.length} analysis jobs still pending`);
      }
      
      // All analysis jobs completed or failed
      console.log('Zen: All analysis jobs have completed or failed');
      zenJob.status = 'completed';
      zenJob.progressItems.push({
        text: 'All analysis jobs completed',
        timestamp: new Date().toISOString()
      });
      updateZenJob(zenJob);
      console.log('Zen: Marked overall job as completed');
      
    } catch (err) {
      console.error('Error monitoring analysis jobs:', err);
      zenJob.progressItems.push({
        text: `Error monitoring analysis jobs: ${err.message}`,
        timestamp: new Date().toISOString()
      });
      zenJob.status = 'error';
      updateZenJob(zenJob);
    }
  };
  
  // Helper to get user-friendly label for analysis type
  const getAnalysisTypeLabel = (type) => {
    switch (type) {
      case 'executiveSummary': return 'Executive Summary';
      case 'visualSiteMap': return 'Visual Site Map';
      case 'contentModel': return 'Content Model';
      case 'competitiveAnalysis': return 'Competitive Analysis';
      default: return 'Analysis';
    }
  };
  
  // Toggle expanding/collapsing a Zen job
  const toggleZenJobExpanded = (jobId) => {
    console.log(`Zen: Toggling expansion state for job ${jobId}`);
    const updated = zenJobs.map(job => {
      if (job.id === jobId) {
        const newState = !job.expanded;
        console.log(`Zen: Job ${jobId} expanded state changing to ${newState}`);
        return { ...job, expanded: newState };
      }
      return job;
    });
    setZenJobs(updated);
    saveZenJobs(updated);
  };
  
  // Delete a Zen job
  const deleteZenJob = (jobId, event) => {
    // Stop event propagation to prevent toggling expansion
    if (event) {
      event.stopPropagation();
    }
    
    console.log(`Zen: Deleting job ${jobId}`);
    
    // Filter out the job to be deleted
    const updatedJobs = zenJobs.filter(job => job.id !== jobId);
    console.log(`Zen: Jobs remaining after deletion: ${updatedJobs.length}`);
    
    // Update state and localStorage
    setZenJobs(updatedJobs);
    saveZenJobs(updatedJobs);
  };
  
  // Delete all completed jobs
  const deleteCompletedJobs = () => {
    console.log('Zen: Deleting all completed jobs');
    
    // Filter out completed jobs
    const updatedJobs = zenJobs.filter(job => 
      job.status !== 'completed' && job.status !== 'error'
    );
    
    console.log(`Zen: Deleted ${zenJobs.length - updatedJobs.length} completed jobs, ${updatedJobs.length} active jobs remaining`);
    
    // Update state and localStorage
    setZenJobs(updatedJobs);
    saveZenJobs(updatedJobs);
  };
  
  // Toggle expanding/collapsing an analysis result
  const toggleAnalysisExpanded = (zenJobId, analysisType) => {
    const updated = zenJobs.map(job => {
      if (job.id === zenJobId) {
        const updatedAnalysisJobs = job.analysisJobs.map(analysis => {
          if (analysis.type === analysisType) {
            return { ...analysis, expanded: !analysis.expanded };
          }
          return analysis;
        });
        return { ...job, analysisJobs: updatedAnalysisJobs };
      }
      return job;
    });
    setZenJobs(updated);
    saveZenJobs(updated);
  };

  if (isLoading) {
    return <Loading message="Loading..." />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Zen Mode</h1>
        <p className="text-lg text-gray-600">
          A simplified approach to site analysis with key insights
        </p>
      </div> */}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">Error: {error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Form */}
      <div className="bg-white shadow-lg rounded-lg p-8 mb-8">
        




        {/* Analysis Options */}
        <div className="mb-6">
            {/* Select Analysis Options */}
            {/* Analysis Types */}
            {/* Analysis */}
          <h3 className="text-lg font-medium text-gray-700 mb-3">
          Analyses to perform
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Executive Summary */}
            <div 
              className={`border rounded-lg p-4 cursor-pointer ${
                selectedOptions.executiveSummary 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onClick={() => toggleOption('executiveSummary')}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 h-6 w-6 mt-0.5 ${
                  selectedOptions.executiveSummary ? 'text-blue-600' : 'text-gray-400'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className={`text-base font-medium ${
                    selectedOptions.executiveSummary ? 'text-blue-800' : 'text-gray-900'
                  }`}>
                    Executive Summary
                  </h4>
                  <p className={`mt-1 text-sm ${
                    selectedOptions.executiveSummary ? 'text-blue-700' : 'text-gray-500'
                  }`}>
                    A comprehensive overview of the website's content, features, and recommendations.
                  </p>
                </div>
              </div>
            </div>

            {/* Visual Site Map */}
            <div 
              className={`border rounded-lg p-4 cursor-pointer ${
                selectedOptions.visualSiteMap 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onClick={() => toggleOption('visualSiteMap')}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 h-6 w-6 mt-0.5 ${
                  selectedOptions.visualSiteMap ? 'text-blue-600' : 'text-gray-400'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className={`text-base font-medium ${
                    selectedOptions.visualSiteMap ? 'text-blue-800' : 'text-gray-900'
                  }`}>
                    Visual Site Map
                  </h4>
                  <p className={`mt-1 text-sm ${
                    selectedOptions.visualSiteMap ? 'text-blue-700' : 'text-gray-500'
                  }`}>
                    A visual representation of the website's structure and navigation hierarchy.
                  </p>
                </div>
              </div>
            </div>

            {/* Content Model */}
            <div 
              className={`border rounded-lg p-4 cursor-pointer ${
                selectedOptions.contentModel 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onClick={() => toggleOption('contentModel')}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 h-6 w-6 mt-0.5 ${
                  selectedOptions.contentModel ? 'text-blue-600' : 'text-gray-400'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className={`text-base font-medium ${
                    selectedOptions.contentModel ? 'text-blue-800' : 'text-gray-900'
                  }`}>
                    Content Model
                  </h4>
                  <p className={`mt-1 text-sm ${
                    selectedOptions.contentModel ? 'text-blue-700' : 'text-gray-500'
                  }`}>
                    A detailed breakdown of content types, templates, and relationships within the site.
                  </p>
                </div>
              </div>
            </div>

            {/* Competitive Analysis */}
            <div 
              className={`border rounded-lg p-4 cursor-pointer ${
                selectedOptions.competitiveAnalysis 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onClick={() => toggleOption('competitiveAnalysis')}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 h-6 w-6 mt-0.5 ${
                  selectedOptions.competitiveAnalysis ? 'text-blue-600' : 'text-gray-400'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className={`text-base font-medium ${
                    selectedOptions.competitiveAnalysis ? 'text-blue-800' : 'text-gray-900'
                  }`}>
                    Competitive Analysis
                  </h4>
                  <p className={`mt-1 text-sm ${
                    selectedOptions.competitiveAnalysis ? 'text-blue-700' : 'text-gray-500'
                  }`}>
                    Compare your website against competitors to identify strengths, weaknesses, and opportunities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Browser Sessions Warning */}
        {browserSessions.length > 0 && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.515 2.625H3.72c-1.345 0-2.188-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Cloud browser sessions are currently in use by another process. 
                  If you want to analyze a website whose content hasn't yet been downloaded, please check back later.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Client URL */}
        <div className="mb-6">
          <label htmlFor="clientUrl" className="block text-lg font-medium text-gray-700 mb-2">
            Client website
          </label>
          <div className="relative">
            <input
              type="url"
              id="clientUrl"
              name="clientUrl"
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full py-3 px-4 text-lg border-2 border-gray-300 rounded-md"
              placeholder="https://example.com"
              value={clientUrl}
              onChange={handleClientUrlChange}
              onFocus={() => setShowClientSuggestions(clientUrlSuggestions.length > 0)}
              onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
              ref={clientInputRef}
              required
            />
            
            {/* URL Suggestions Dropdown */}
            {showClientSuggestions && (
              <div className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                {clientUrlSuggestions.map((url, index) => (
                  <div
                    key={index}
                    className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                    onClick={() => selectClientUrlSuggestion(url)}
                  >
                    {url}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        

        {/* Competitor URLs (only shown if competitive analysis is selected) */}
        {selectedOptions.competitiveAnalysis && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-700 mb-3">
              Competitor websites
            </h3>
            
            {competitorUrls.map((url, index) => (
              <div key={index} className="flex items-center mb-3">
                <div className="flex-grow relative">
                  <input
                    type="url"
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full py-2 px-4 border-2 border-gray-300 rounded-md"
                    placeholder={`Competitor ${index + 1} URL`}
                    value={url}
                    onChange={(e) => handleCompetitorUrlChange(index, e.target.value)}
                    onFocus={() => {
                      // Filter suggestions based on current text value
                      if (url) {
                        const filtered = completedScrapeJobs.map(job => job.targetUrl).filter(
                          suggestion => suggestion.toLowerCase().includes(url.toLowerCase())
                        );
                        setCompetitorUrlSuggestions(filtered.length > 0 ? filtered : []);
                        setShowCompetitorSuggestions(filtered.length > 0);
                      } else {
                        // Show all suggestions if input is empty
                        const allUrls = completedScrapeJobs.map(job => job.targetUrl);
                        setCompetitorUrlSuggestions(allUrls);
                        setShowCompetitorSuggestions(allUrls.length > 0);
                      }
                      setActiveCompetitorIndex(index);
                    }}
                    onBlur={() => setTimeout(() => setShowCompetitorSuggestions(false), 200)}
                  />
                  
                  {/* URL Suggestions Dropdown */}
                  {showCompetitorSuggestions && activeCompetitorIndex === index && (
                    <div className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                      {competitorUrlSuggestions.map((suggestionUrl, suggestionIndex) => (
                        <div
                          key={suggestionIndex}
                          className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                          onClick={() => selectCompetitorUrlSuggestion(suggestionUrl, index)}
                        >
                          {suggestionUrl}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="ml-2 flex">
                  {index === competitorUrls.length - 1 ? (
                    <button
                      type="button"
                      onClick={addCompetitorUrl}
                      className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeCompetitorUrl(index)}
                      className="p-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Files Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-lg font-medium text-gray-700">
              Additional files (optional)
            </label>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                multiple
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.html,.htm,.md,.csv,.json,.xml"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Upload Files
              </button>
            </div>
          </div>
          
          {/* Error message for file uploads */}
          {fileError && (
            <p className="mt-1 text-sm text-red-600">{fileError}</p>
          )}
          
          {/* File Selection List */}
          <div className="mt-3 border border-gray-300 rounded-md max-h-60 overflow-y-auto p-2">
            {isLoadingFiles ? (
              <div className="flex justify-center items-center h-20">
                <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : allFiles.length === 0 ? (
              <div className="text-center text-gray-500 py-4">
                <p>No files available. Upload files to analyze.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {allFiles.map(file => (
                  <label
                    key={file.fileId}
                    className={`flex items-center p-2 rounded hover:bg-gray-50 cursor-pointer ${
                      selectedFiles.includes(file.fileId) ? 'bg-blue-50 border border-blue-200' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.fileId)}
                      onChange={() => toggleFileSelection(file.fileId)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 mr-3"
                    />
                    <div className="ml-2 flex-grow overflow-hidden">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.filename || 'Unnamed file'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {file.type || 'Unknown type'} 
                        {file.size ? ` • ${Math.round(file.size / 1024)} KB` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteFile(file.fileId);
                      }}
                      className="ml-2 p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100"
                      title="Delete file"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Start Analysis button */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={startAnalysis}
            disabled={isSubmitting || !clientUrl}
            className={`px-6 py-3 text-lg font-medium rounded-md shadow-sm ${
              isSubmitting || !clientUrl
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 inline-block" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Starting Analyses...
              </>
            ) : (
              'Start Analyses'
            )}
          </button>
        </div>
      </div>

      {/* Debug info (helpful for development) */}
      {/* <div className="mt-4 p-2 bg-gray-100 rounded">
        <p>Jobs count: {zenJobs.length}</p>
        <p>Status: {zenJobs.length > 0 ? zenJobs[0].status : 'None'}</p>
      </div> */}

      {/* Job History */}
      {zenJobs.length > 0 && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">My Analyses</h2>
            
            {/* Only show Clear Completed button if there are completed or error jobs */}
            {/* {zenJobs.some(job => job.status === 'completed' || job.status === 'error') && (
              <button
                onClick={deleteCompletedJobs}
                className="px-3 py-1 text-sm text-gray-600 hover:text-red-500 border border-gray-300 hover:border-red-300 rounded"
              >
                Clear Completed Jobs
              </button>
            )} */}
          </div>
          
          <div className="space-y-6">
            {zenJobs.map((job) => (
              <div key={job.id} className="bg-white shadow rounded-lg overflow-hidden">
                {/* Job Header */}
                <div 
                  className="bg-white p-4 flex justify-between items-center cursor-pointer border-b"
                  onClick={() => toggleZenJobExpanded(job.id)}
                >
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {job.clientUrl}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Created: {new Date(job.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <span className={`mr-2 px-2 py-1 text-xs font-medium rounded-full ${
                      job.status === 'completed' ? 'bg-green-100 text-green-800' :
                      job.status === 'error' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {job.status === 'scraping' ? 'Downloading Content' :
                       job.status === 'analyzing' ? 'Analyzing' :
                       job.status === 'completed' ? 'Completed' :
                       job.status === 'error' ? 'Error' : 'Processing'}
                    </span>
                    
                    {/* Delete job button */}
                    <button 
                      onClick={(e) => deleteZenJob(job.id, e)}
                      className="ml-2 mr-1 p-1 text-gray-400 hover:text-red-500 rounded"
                      title="Delete job"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    <svg 
                      className={`h-5 w-5 text-gray-500 transform transition-transform ${job.expanded ? 'rotate-180' : ''}`}
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 20 20" 
                      fill="currentColor"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                
                {/* Job Details (expanded view) */}
                {job.expanded && (
                  <div className="p-4">
                    {/* Simple Status Line */}
                    {/* {job.status !== 'completed' && */}
                    <div className="bg-white p-3 mb-4 rounded border border-gray-200">
                      <div className="flex items-center">
                        <div className={`h-2.5 w-2.5 rounded-full mr-2 ${
                          job.status === 'completed' ? 'bg-green-500' :
                          job.status === 'error' ? 'bg-red-500' :
                          job.status === 'analyzing' ? 'bg-blue-500' :
                          job.status === 'scraping' ? 'bg-indigo-500' :
                          'bg-gray-500'
                        }`}></div>
                        <p className="text-sm text-gray-700">
                          {job.status === 'scraping' ? 'Downloading website content for analysis...' :
                           job.status === 'analyzing' ? 'Analyzing website content and generating insights...' :
                           job.status === 'completed' ? 'Analysis complete. Results available below.' :
                           job.status === 'error' ? 'An error occurred during the analysis process.' :
                           'Preparing to analyze website...'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Analysis Results */}
                    {job.analysisJobs && job.analysisJobs.length > 0 && (
                      <div className="mt-6">
                        {/* <h4 className="text-lg font-medium text-gray-900 mb-3">Analysis Results</h4> */}
                        
                        <div className="space-y-4">
                          {job.analysisJobs.map((analysis) => (
                            <div key={analysis.jobId} className="border border-gray-200 rounded-lg overflow-hidden">
                              {/* Analysis Header */}
                              <div 
                                className={`p-3 flex justify-between items-center cursor-pointer border-b ${
                                  analysis.status === 'completed' ? 'bg-white text-green-800' : 'bg-white text-gray-800'
                                }`}
                                onClick={() => toggleAnalysisExpanded(job.id, analysis.type)}
                              >
                                <div className="flex items-center">
                                  <div className={`flex-shrink-0 h-6 w-6 ${
                                    analysis.status === 'completed' ? 'text-green-600' : 'text-gray-400'
                                  }`}>
                                    {analysis.type === 'executiveSummary' && (
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                      </svg>
                                    )}
                                    {analysis.type === 'visualSiteMap' && (
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                                      </svg>
                                    )}
                                    {analysis.type === 'contentModel' && (
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                                      </svg>
                                    )}
                                    {analysis.type === 'competitiveAnalysis' && (
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                                      </svg>
                                    )}
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-base font-medium text-gray-900">
                                      {getAnalysisTypeLabel(analysis.type)}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {analysis.status === 'completed' ? '' : 
                                       analysis.status === 'failed' ? 'Analysis failed' : 'In progress...'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center">
                                  {/* <span className={`mr-2 px-2 py-1 text-xs font-medium rounded-full ${
                                    analysis.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    analysis.status === 'failed' ? 'bg-red-100 text-red-800' :
                                    'bg-blue-100 text-blue-800'
                                  }`}>
                                    {analysis.status}
                                  </span> */}
                                  <svg 
                                    className={`h-5 w-5 text-gray-500 transform transition-transform ${analysis.expanded ? 'rotate-180' : ''}`}
                                    xmlns="http://www.w3.org/2000/svg" 
                                    viewBox="0 0 20 20" 
                                    fill="currentColor"
                                  >
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>
                               {/* {JSON.stringify(analysis)} */}
                              {/* Analysis Content */}
                              {(analysis.expanded === undefined ? true : analysis.expanded) && (
                                <div className="p-4 bg-white">
                                  {analysis.status === 'completed' && analysis.resultText ? (
                                    <div className="prose max-w-none">
                                      <ReactMarkdown>{analysis.resultText}</ReactMarkdown>
                                    </div>
                                  ) : analysis.status === 'failed' ? (
                                    <div className="text-red-600">
                                      Analysis failed. Please try again.
                                    </div>
                                  ) : (
                                    <div className="flex justify-center p-4">
                                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                    </div>
                                  )}
                                        {/* Go to detailed analysis page */}
                                  
                                  {analysis.status === 'completed' && (
                                    <div className="mt-4 flex justify-end">
                                      <button
                                        onClick={() => navigate(`/advanced/analysis-jobs/${analysis.jobId}`)}
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                      > View analysis in advanced site
                                      </button>
                                     
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Zen;